/**
 * 模板路由 - 根据当前模板ID选择对应的页面组件
 * 
 * 这个文件是路由入口点，根据用户选择的模板动态加载对应版本的页面
 */

import { useTemplate, SITE_TEMPLATES } from "@/context/TemplateContext";

// ============================================
// 首页模板组件
// ============================================

import ClassicHomePage from "@/templates/classic/home/HomePage";
import ModernHomePage from "@/templates/modern/home/HomePage";

// 深色商务 + 清新绿意
import DarkHomePage from "@/templates/dark/home/HomePage";
import GreenHomePage from "@/templates/green/home/HomePage";
import RedHomePage from "@/templates/red/home/HomePage";
import TechBlueHomePage from "@/templates/tech-blue/home/HomePage";
import DeepBlueHomePage from "@/templates/deep-blue/home/HomePage";
import TealHomePage from "@/templates/teal/home/HomePage";
import SunsetHomePage from "@/templates/sunset/home/HomePage";
import PurpleHomePage from "@/templates/purple/home/HomePage";
import CrystalHomePage from "@/templates/crystal/home/HomePage";

// ============================================
// 列表页模板组件
// ============================================

import ClassicCategoryPage from "@/templates/classic/category/CategoryPage";
import ModernCategoryPage from "@/templates/modern/category/CategoryPage";
import DarkCategoryPage from "@/templates/dark/category/CategoryPage";
import GreenCategoryPage from "@/templates/green/category/CategoryPage";
import RedCategoryPage from "@/templates/red/category/CategoryPage";
import TechBlueCategoryPage from "@/templates/tech-blue/category/CategoryPage";
import DeepBlueCategoryPage from "@/templates/deep-blue/category/CategoryPage";
import TealCategoryPage from "@/templates/teal/category/CategoryPage";
import SunsetCategoryPage from "@/templates/sunset/category/CategoryPage";
import PurpleCategoryPage from "@/templates/purple/category/CategoryPage";
import CrystalCategoryPage from "@/templates/crystal/category/CategoryPage";

// ============================================
// 详情页模板组件
// ============================================

import ClassicListingDetail from "@/templates/classic/listing-detail/ListingDetail";
import ModernListingDetail from "@/templates/modern/listing-detail/ListingDetail";
import DarkListingDetail from "@/templates/dark/listing-detail/ListingDetail";
import GreenListingDetail from "@/templates/green/listing-detail/ListingDetail";
import RedListingDetail from "@/templates/red/listing-detail/ListingDetail";
import TechBlueListingDetail from "@/templates/tech-blue/listing-detail/ListingDetail";
import DeepBlueListingDetail from "@/templates/deep-blue/listing-detail/ListingDetail";
import TealListingDetail from "@/templates/teal/listing-detail/ListingDetail";
import SunsetListingDetail from "@/templates/sunset/listing-detail/ListingDetail";
import PurpleListingDetail from "@/templates/purple/listing-detail/ListingDetail";
import CrystalListingDetail from "@/templates/crystal/listing-detail/ListingDetail";

// ============================================
// 登录页模板组件
// ============================================

import SharedLoginPage from "@/pages/login/page";
import RedLoginPage from "@/templates/red/login/LoginPage";
import TechBlueLoginPage from "@/templates/tech-blue/login/LoginPage";
import DeepBlueLoginPage from "@/templates/deep-blue/login/LoginPage";
import TealLoginPage from "@/templates/teal/login/LoginPage";
import SunsetLoginPage from "@/templates/sunset/login/LoginPage";
import PurpleLoginPage from "@/templates/purple/login/LoginPage";
import CrystalLoginPage from "@/templates/crystal/login/LoginPage";

// ============================================
// 注册页模板组件
// ============================================

import SharedRegisterPage from "@/pages/register/page";
import RedRegisterPage from "@/templates/red/register/RegisterPage";
import TechBlueRegisterPage from "@/templates/tech-blue/register/RegisterPage";
import DeepBlueRegisterPage from "@/templates/deep-blue/register/RegisterPage";
import TealRegisterPage from "@/templates/teal/register/RegisterPage";
import SunsetRegisterPage from "@/templates/sunset/register/RegisterPage";
import PurpleRegisterPage from "@/templates/purple/register/RegisterPage";
import CrystalRegisterPage from "@/templates/crystal/register/RegisterPage";

// ============================================
// 单页模板组件
// ============================================

import ClassicSinglePage from "@/templates/classic/single-page/SinglePage";
import SunsetSinglePage from "@/templates/sunset/single-page/SinglePage";
import PurpleSinglePage from "@/templates/purple/single-page/SinglePage";
import CrystalSinglePage from "@/templates/crystal/single-page/SinglePage";

// ============================================
// VIP页模板组件
// ============================================

import SharedVipPage from "@/pages/vip/page";
import RedVipPage from "@/templates/red/vip/VipPage";
import TechBlueVipPage from "@/templates/tech-blue/vip/VipPage";
import DeepBlueVipPage from "@/templates/deep-blue/vip/VipPage";
import TealVipPage from "@/templates/teal/vip/VipPage";
import SunsetVipPage from "@/templates/sunset/vip/VipPage";
import PurpleVipPage from "@/templates/purple/vip/VipPage";
import CrystalVipPage from "@/templates/crystal/vip/VipPage";
import ModernSinglePage from "@/templates/modern/single-page/SinglePage";
import DarkSinglePage from "@/templates/classic/single-page/SinglePage"; // TODO
import GreenSinglePage from "@/templates/classic/single-page/SinglePage"; // TODO
import RedSinglePage from "@/templates/classic/single-page/SinglePage";   // TODO

// ============================================
// 模板组件映射
// ============================================

const homeTemplates = {
  classic: ClassicHomePage,
  modern: ModernHomePage,
  dark: DarkHomePage,
  green: GreenHomePage,
  red: RedHomePage,
  "tech-blue": TechBlueHomePage,
  "deep-blue": DeepBlueHomePage,
  teal: TealHomePage,
  sunset: SunsetHomePage,
  purple: PurpleHomePage,
  crystal: CrystalHomePage,
};

const categoryTemplates = {
  classic: ClassicCategoryPage,
  modern: ModernCategoryPage,
  dark: DarkCategoryPage,
  green: GreenCategoryPage,
  red: RedCategoryPage,
  "tech-blue": TechBlueCategoryPage,
  "deep-blue": DeepBlueCategoryPage,
  teal: TealCategoryPage,
  sunset: SunsetCategoryPage,
  purple: PurpleCategoryPage,
  crystal: CrystalCategoryPage,
};

const listingDetailTemplates = {
  classic: ClassicListingDetail,
  modern: ModernListingDetail,
  dark: DarkListingDetail,
  green: GreenListingDetail,
  red: RedListingDetail,
  "tech-blue": TechBlueListingDetail,
  "deep-blue": DeepBlueListingDetail,
  teal: TealListingDetail,
  sunset: SunsetListingDetail,
  purple: PurpleListingDetail,
  crystal: CrystalListingDetail,
};

const loginTemplates = {
  classic: SharedLoginPage,
  modern: SharedLoginPage,
  dark: SharedLoginPage,
  green: SharedLoginPage,
  red: RedLoginPage,
  "tech-blue": TechBlueLoginPage,
  "deep-blue": DeepBlueLoginPage,
  teal: TealLoginPage,
  sunset: SunsetLoginPage,
  purple: PurpleLoginPage,
  crystal: CrystalLoginPage,
};

const registerTemplates = {
  classic: SharedRegisterPage,
  modern: SharedRegisterPage,
  dark: SharedRegisterPage,
  green: SharedRegisterPage,
  red: RedRegisterPage,
  "tech-blue": TechBlueRegisterPage,
  "deep-blue": DeepBlueRegisterPage,
  teal: TealRegisterPage,
  sunset: SunsetRegisterPage,
  purple: PurpleRegisterPage,
  crystal: CrystalRegisterPage,
};

const singlePageTemplates = {
  classic: ClassicSinglePage,
  modern: ModernSinglePage,
  dark: DarkSinglePage,
  green: GreenSinglePage,
  red: RedSinglePage,
  "tech-blue": ClassicSinglePage,
  "deep-blue": ClassicSinglePage,
  teal: ModernSinglePage,
  sunset: SunsetSinglePage,
  purple: PurpleSinglePage,
  crystal: CrystalSinglePage,
};

const vipTemplates = {
  classic: SharedVipPage,
  modern: SharedVipPage,
  dark: SharedVipPage,
  green: SharedVipPage,
  red: RedVipPage,
  "tech-blue": TechBlueVipPage,
  "deep-blue": DeepBlueVipPage,
  teal: TealVipPage,
  sunset: SunsetVipPage,
  purple: PurpleVipPage,
  crystal: CrystalVipPage,
};

/**
 * 获取首页模板组件
 */
export function useHomeTemplate() {
  const { templateId } = useTemplate();
  const Template = homeTemplates[templateId as keyof typeof homeTemplates] || ClassicHomePage;
  return Template;
}

/**
 * 获取列表页模板组件
 */
export function useCategoryTemplate() {
  const { templateId } = useTemplate();
  const Template = categoryTemplates[templateId as keyof typeof categoryTemplates] || ClassicCategoryPage;
  return Template;
}

/**
 * 获取详情页模板组件
 */
export function useListingDetailTemplate() {
  const { templateId } = useTemplate();
  const Template = listingDetailTemplates[templateId as keyof typeof listingDetailTemplates] || ClassicListingDetail;
  return Template;
}

/**
 * 获取登录页模板组件
 */
export function useLoginTemplate() {
  const { templateId } = useTemplate();
  const Template = loginTemplates[templateId as keyof typeof loginTemplates] || SharedLoginPage;
  return Template;
}

/**
 * 获取注册页模板组件
 */
export function useRegisterTemplate() {
  const { templateId } = useTemplate();
  const Template = registerTemplates[templateId as keyof typeof registerTemplates] || SharedRegisterPage;
  return Template;
}

/**
 * 获取单页模板组件
 */
export function useSinglePageTemplate() {
  const { templateId } = useTemplate();
  const Template = singlePageTemplates[templateId as keyof typeof singlePageTemplates] || ClassicSinglePage;
  return Template;
}

/**
 * 获取VIP页模板组件
 */
export function useVipTemplate() {
  const { templateId } = useTemplate();
  const Template = vipTemplates[templateId as keyof typeof vipTemplates] || SharedVipPage;
  return Template;
}

// ============================================
// 导出模板配置
// ============================================

export { SITE_TEMPLATES };
